import React, { FunctionComponent, useEffect, useState } from 'react'
import styles from './TagPicker.module.sass'
import ShadowContainer from '../../../etc/popover/ShadowContainer/ShadowContainer'
import Field from '../../../controls/Field/Field'
import { apiRequests } from '../../../../http/config'
import { groupBy } from 'fp-ts/es6/NonEmptyArray'
import { ordString } from 'fp-ts/es6/Ord'
import Button, { ButtonStyle } from '../../../controls/Button/Button'
import { snoc } from '../../../../utils/utils'
import { entityRemove, eqEntity } from '../../../../utils/entity-utils'
import { isNonEmpty, uniq } from 'fp-ts/es6/Array'
import InfiniteScroll from '../../../etc/scroll/InfiniteScroll'
import { pipe } from 'fp-ts/es6/function'
import { Tag } from '../../../../responses/tags'
import TagList from '../TagList/TagList'
import { useSubject } from '../../../../utils/hooks'
import { fromQuery } from '../../../../utils/rx-operators'
import Popover from '../../../etc/Popover'
import TagAddIcon from '../../../../../files/tag/tag-add.svg'
import CloseIcon from 'files/close.svg'
import SearchIcon from 'files/search.svg'
import { Colors } from '../../../../styles'

const groupFlow = groupBy((tag: Tag) => tag.name[0])

interface Props {
    value: Tag[],
    onChange: (tags: Tag[]) => void,
}

const TagPicker: FunctionComponent<Props> = props => {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')

    const [data$, data] = useSubject<string, Tag[]>([], source$ => source$.pipe(
        fromQuery(query => apiRequests.tag.list({ query })),
    ))

    useEffect(() => data$.next(search), [data$, search])

    return <Popover
        isOpen={open}
        onClickOutside={() => setOpen(false)}
        reposition={false}
        positions={['bottom']}
        align={'end'}
        content={<ShadowContainer className={styles.popover}>
            <div className={styles.header}>
                <div className={styles.title}>
                    Найти метки
                </div>
                {
                    isNonEmpty(props.value) && <div className={styles.tags}>
                        <TagList
                            className={styles.list}
                            delete={true}
                            tags={props.value}
                            onClick={tag => pipe(props.value, entityRemove(tag), props.onChange)}
                        />
                        <Button
                            className={styles.button}
                            buttonStyle={ButtonStyle.TINTED}
                            color={Colors.red}
                            onClick={() => props.onChange([])}
                        >
                            <CloseIcon/>
                        </Button>
                    </div>
                }
                <Field
                    block
                    className={styles.field}
                    value={search}
                    onChange={setSearch}
                    placeholder={'Поиск'}
                    rightItem={<SearchIcon/>}
                />
            </div>
            {
                isNonEmpty(data) && <InfiniteScroll
                    className={styles.tagList}
                    onReach={() => data$.next(search)}
                >
                    {
                        Object.entries(groupFlow(data))
                            .sort(([x], [y]) => ordString.compare(x, y))
                            .map(([key, tags]) =>
                                <div key={key} className={styles.tagGroup}>
                                    <div className={styles.capital}>{key.toUpperCase()}</div>
                                    <TagList
                                        className={styles.tags}
                                        tags={tags}
                                        onClick={tag => pipe(props.value, snoc(tag), uniq<Tag>(eqEntity), props.onChange)}
                                    />
                                </div>)
                    }
                </InfiniteScroll>
            }
        </ShadowContainer>}
    >
        <Button
            className={styles.openButton}
            title={{direction: 'up', content: 'Фильтр по тегам'}}
            active={props.value.length > 0}
            onClick={() => setOpen(!open)}
        >
            <TagAddIcon/>
        </Button>
    </Popover>
}

export default TagPicker